import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getAddressSuggestions, AddressPrediction } from '../api/addressApi/addressApi';
import useSnackBar from '../hooks/useSnackBar';
import { AxiosError } from 'axios';
import { ErrorData } from '../types';


const useStyles = makeStyles()(() => ({
    dialogTitle: {
        color: '#ffffff',
        backgroundColor: '#2d3748',
        fontWeight: 600,
        fontSize: '25px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    dialogContent: {
        backgroundColor: '#2d3748',
        color: '#ffffff',
        padding: '24px',
    },
    dialogActions: {
        backgroundColor: '#2d3748',
        padding: '16px 24px',
        gap: '12px',
    },
    messageText: {
        fontSize: '18px',
        lineHeight: '24px',
        marginBottom: '16px',
    },
    addressItem: {
        fontSize: '14px',
        color: '#ffffff',
        marginBottom: '8px',
        padding: '8px 12px',
        backgroundColor: 'rgba(79, 209, 199, 0.1)',
        borderRadius: '6px',
        border: '1px solid rgba(79, 209, 199, 0.3)',
    },
    addressLabel: {
        fontWeight: 600,
        marginRight: '8px',
    },
    cancelButton: {
        color: '#4fd1c7',
        border: '1px solid #4fd1c7',
        borderRadius: '8px',
        padding: '10px 24px',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: 'rgba(79, 209, 199, 0.08)',
            borderColor: '#4fd1c7',
        },
    },
    confirmButton: {
        backgroundColor: '#4fd1c7',
        color: '#2d3748',
        borderRadius: '8px',
        padding: '10px 24px',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#3dd1c6',
        },
    },
    suggestionsSection: {
        marginTop: '20px',
        padding: '16px 0',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    },
    suggestionsList: {
        backgroundColor: '#1a202c',
        borderRadius: '8px',
        maxHeight: '200px',
        overflow: 'auto',
        padding: 0,
    },
    suggestionItem: {
        backgroundColor: '#2d3748',
        marginBottom: '4px',
        // borderRadius: '6px',
        padding: '12px 16px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#4a5568',
        },
        '&:last-child': {
            marginBottom: 0,
        },
    },
    suggestionText: {
        color: '#ffffff',
        fontSize: '14px',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    },
}));

export interface ConfirmationAddress {
    label: string;
    value: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
}

interface ConfirmationPopupProps {
    open: boolean;
    title?: string;
    message?: string;
    addresses?: ConfirmationAddress[];
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel: () => void;
    onAddressSuggestionSelect?: (address: AddressPrediction, addressType: string) => void;
    confirmButtonClassName?: string;
    cancelButtonClassName?: string;
}

const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({
    open,
    title = 'Confirm Custom Address',
    message = 'You have entered custom address information. Are you sure you want to proceed with this address?',
    addresses = [],
    confirmText = 'Proceed',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    onAddressSuggestionSelect,
    confirmButtonClassName,
    cancelButtonClassName,
}) => {
    const { classes } = useStyles();
    const [addressSuggestions, setAddressSuggestions] = useState<{ [key: string]: AddressPrediction[] }>({});
    const [loadingSuggestions, setLoadingSuggestions] = useState<{ [key: string]: boolean }>({});
    const { setAlert } = useSnackBar();

    // Function to construct full address string from components
    const constructAddressString = (address: ConfirmationAddress) => {
        const parts = [
            address.addressLine1,
            address.addressLine2,
            address.city,
            address.state,
            address.zipcode
        ].filter(Boolean);
        return parts.join(', ');
    };

    // Fetch address suggestions for each custom address
    useEffect(() => {
        if (open && addresses.length > 0) {
            addresses.forEach((address, index) => {
                if (address.addressLine1) {
                    const addressKey = `${address.label}_${index}`;
                    setLoadingSuggestions(prev => ({ ...prev, [addressKey]: true }));

                    const fullAddress = constructAddressString(address);
                    getAddressSuggestions(fullAddress)
                        .then((response) => {
                            setAddressSuggestions(prev => ({
                                ...prev,
                                [addressKey]: response.data.predictions || []
                            }));
                        })
                        .catch((error: AxiosError<ErrorData>) => {
                            console.error('Error fetching address suggestions:', error);
                            setAlert((prev) => ({
                                ...prev,
                                message: error.response?.data.message || 'Failed to load account data',
                                type: 'error',
                            }));
                            setAddressSuggestions(prev => ({ ...prev, [addressKey]: [] }));
                        })
                        .finally(() => {
                            setLoadingSuggestions(prev => ({ ...prev, [addressKey]: false }));
                        });
                }
            });
        }
    }, [open, addresses]);

    // Handle suggestion selection
    const handleSuggestionSelect = (suggestion: AddressPrediction, addressType: string) => {
        if (onAddressSuggestionSelect) {
            onAddressSuggestionSelect(suggestion, addressType);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth='sm'
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#2d3748',
                    borderRadius: '12px',
                },
            }}
        >
            <DialogTitle className={classes.dialogTitle}>
                {title}
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                <Typography className={classes.messageText}>
                    {message}
                </Typography>

                {addresses.length > 0 && (
                    <Box>
                        {addresses.map((address, index) => {
                            const addressKey = `${address.label}_${index}`;
                            const suggestions = addressSuggestions[addressKey] || [];
                            const isLoading = loadingSuggestions[addressKey];

                            return (
                                <Box key={index}>
                                    <Box className={classes.addressItem}>
                                        <span className={classes.addressLabel}>{address.label}:</span>
                                        {constructAddressString(address)}
                                    </Box>

                                    {/* Address Suggestions Section */}
                                    <Box className={classes.suggestionsSection}>
                                        <Typography variant="body2" sx={{ color: '#a0aec0', marginBottom: '12px', fontWeight: 500 }}>
                                            Suggested addresses for {address.label}:
                                        </Typography>

                                        {isLoading ? (
                                            <Box className={classes.loadingContainer}>
                                                <CircularProgress size={20} sx={{ color: '#4fd1c7' }} />
                                                <Typography variant="body2" sx={{ marginLeft: '8px', color: '#a0aec0' }}>
                                                    Loading suggestions...
                                                </Typography>
                                            </Box>
                                        ) : suggestions.length > 0 ? (
                                            <List className={classes.suggestionsList}>
                                                {suggestions.map((suggestion, suggestionIndex) => (
                                                    <ListItem
                                                        key={suggestionIndex}
                                                        className={classes.suggestionItem}
                                                        onClick={() => handleSuggestionSelect(suggestion, address.label)}
                                                    >
                                                        <ListItemText
                                                            primary={suggestion.description}
                                                            classes={{
                                                                primary: classes.suggestionText,
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: '#718096' }}>
                                                No address suggestions found
                                            </Typography>
                                        )}
                                    </Box>

                                    {index < addresses.length - 1 && <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '16px 0' }} />}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={onCancel}
                    className={`${classes.cancelButton} ${cancelButtonClassName || ''}`.trim()}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    className={`${classes.confirmButton} ${confirmButtonClassName || ''}`.trim()}
                    variant='contained'
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationPopup;